// Cover-PDF font resolution.
//
// Extracted from pdfGenerator.ts so the editor↔PDF font parity can be unit
// tested without pulling pdf-lib / fontkit / heic2any into the test runtime.
//
// The single source of truth for "which typefaces the cover PDF can embed" is
// FONT_URLS. It MUST stay a superset of every family the editor's picker
// (EDITOR_TEXT_FONTS in ./editorFonts) offers — otherwise a font the customer
// sees in the editor silently renders as the Helvetica fallback in the PDF.
// coverFonts.test.ts enforces that invariant.

// jsDelivr mirror of the google/fonts GitHub repo. The `@master` ref serves
// every file even though GitHub shows `main` as the default branch — verified
// manually with curl. Variable-font filenames carry their axis tags in
// brackets, which must be percent-encoded ([ → %5B, , → %2C, ] → %5D).
const CDN = 'https://cdn.jsdelivr.net/gh/google/fonts@master';

export const FONT_URLS: Record<string, string> = {
  'Anton':              `${CDN}/ofl/anton/Anton-Regular.ttf`,
  'Londrina Solid':     `${CDN}/ofl/londrinasolid/LondrinaSolid-Regular.ttf`,
  'Bungee':             `${CDN}/ofl/bungee/Bungee-Regular.ttf`,
  'Cormorant Garamond': `${CDN}/ofl/cormorantgaramond/CormorantGaramond%5Bwght%5D.ttf`,
  'Cormorant SC':       `${CDN}/ofl/cormorantsc/CormorantSC-Medium.ttf`,
  'Playfair Display':   `${CDN}/ofl/playfairdisplay/PlayfairDisplay%5Bwght%5D.ttf`,
  // Arabic letters (2026-09-30) — see editorFonts.ts.
  'Amiri':              `${CDN}/ofl/amiri/Amiri-Regular.ttf`,
  'DM Sans':            `${CDN}/ofl/dmsans/DMSans%5Bopsz%2Cwght%5D.ttf`,
  'Roboto':             `${CDN}/ofl/roboto/Roboto%5Bwdth%2Cwght%5D.ttf`,
  'Oswald':             `${CDN}/ofl/oswald/Oswald%5Bwght%5D.ttf`,
  'Bebas Neue':         `${CDN}/ofl/bebasneue/BebasNeue-Regular.ttf`,
  'Montserrat':         `${CDN}/ofl/montserrat/Montserrat%5Bwght%5D.ttf`,
  'Poppins':            `${CDN}/ofl/poppins/Poppins-Bold.ttf`,
  'Lobster':            `${CDN}/ofl/lobster/Lobster-Regular.ttf`,
  'Pacifico':           `${CDN}/ofl/pacifico/Pacifico-Regular.ttf`,
  // Added so every EDITOR_TEXT_FONTS pick embeds in the PDF (URLs HEAD-checked
  // on jsDelivr — variable fonts use the bracketed axis filename).
  'Fraunces':           `${CDN}/ofl/fraunces/Fraunces%5BSOFT%2CWONK%2Copsz%2Cwght%5D.ttf`,
  'Lora':               `${CDN}/ofl/lora/Lora%5Bwght%5D.ttf`,
  'Roboto Slab':        `${CDN}/apache/robotoslab/RobotoSlab%5Bwght%5D.ttf`,
  'Abril Fatface':      `${CDN}/ofl/abrilfatface/AbrilFatface-Regular.ttf`,
  'Gasoek One':         `${CDN}/ofl/gasoekone/GasoekOne-Regular.ttf`,
  'Inter':              `${CDN}/ofl/inter/Inter%5Bopsz%2Cwght%5D.ttf`,
  'Quicksand':          `${CDN}/ofl/quicksand/Quicksand%5Bwght%5D.ttf`,
  'Fredoka':            `${CDN}/ofl/fredoka/Fredoka%5Bwdth%2Cwght%5D.ttf`,
  'Ruda':               `${CDN}/ofl/ruda/Ruda%5Bwght%5D.ttf`,
  'Great Vibes':        `${CDN}/ofl/greatvibes/GreatVibes-Regular.ttf`,
  'Sacramento':         `${CDN}/ofl/sacramento/Sacramento-Regular.ttf`,
  'Dancing Script':     `${CDN}/ofl/dancingscript/DancingScript%5Bwght%5D.ttf`,
  'Caveat':             `${CDN}/ofl/caveat/Caveat%5Bwght%5D.ttf`,
  'Indie Flower':       `${CDN}/ofl/indieflower/IndieFlower-Regular.ttf`,
  'Shadows Into Light': `${CDN}/ofl/shadowsintolight/ShadowsIntoLight.ttf`,
  'Permanent Marker':   `${CDN}/apache/permanentmarker/PermanentMarker-Regular.ttf`,
  'Amatic SC':          `${CDN}/ofl/amaticsc/AmaticSC-Regular.ttf`,
  'Special Elite':      `${CDN}/apache/specialelite/SpecialElite-Regular.ttf`,
};

const fontBytesCache = new Map<string, ArrayBuffer | null>();

// The editor's font picker stores a CSS family LIST (e.g.
// `"'Fraunces', 'Cormorant Garamond', Georgia, serif"`) on zone overrides +
// spineConfig. FONT_URLS is keyed by the bare primary font name, so we peel
// the first family name off before lookup. A bare name like `"Bebas Neue"`
// already matches verbatim. Returns null when the input is null/empty so
// getFont falls back cleanly.
export function primaryFontName(family: string | null | undefined): string | null {
  if (!family) return null;
  const first = (family.split(',')[0] ?? '').trim();
  return first.replace(/^['"]|['"]$/g, '').trim() || null;
}

// Resolve the bare font name the PDF should embed for a cover text zone.
// The customer's per-zone override (from the sidebar / floating picker) wins
// over the template default. Returns null when neither yields a name, letting
// getFont fall back to Helvetica.
export function resolveZoneFontName(
  overrideFamily: string | null | undefined,
  zoneFamily: string | null | undefined,
): string | null {
  // Normalise BOTH sides. The template default (zoneFamily) can be a full CSS
  // list just like the override; passing it raw to FONT_URLS would miss and
  // fall back to Helvetica even when the customer never touched the picker.
  return primaryFontName(overrideFamily) ?? primaryFontName(zoneFamily);
}

// The spine text mirrors the front-cover TITLE (the `destination` text zone):
// same typeface, same colour, kept in sync with no separate spine setting.
// `resolveTitleZoneStyle` is the single source of truth all three render
// pipelines (editor `CoverView`, preview `CoverSpreadPreview`, PDF
// `generateCoverPdf`) consult so the spine can never drift from the title.
//
// It returns the title zone's resolved style in two font shapes:
//   - `fontFamilyRaw`  — the raw CSS family string (override ?? zone) for
//                        browser rendering (feed through `toCssFontFamily`).
//   - `fontFamilyName` — the bare primary name for the PDF's FONT_URLS embed.
// plus `colorHex` (override ?? zone). Each is null when unavailable so callers
// fall back cleanly (spineConfig, then a hard default).
export interface TitleZoneStyle {
  fontFamilyRaw: string | null;
  fontFamilyName: string | null;
  colorHex: string | null;
}

type TitleZoneLike =
  | { type?: string | null; font_family?: string | null; color?: string | null }
  | null
  | undefined;
type TitleZoneOverrideLike =
  | { fontFamily?: string | null; color?: string | null }
  | null
  | undefined;

export function resolveTitleZoneStyle(
  zones: ReadonlyArray<TitleZoneLike> | null | undefined,
  overrides: Record<number | string, TitleZoneOverrideLike> | null | undefined,
): TitleZoneStyle {
  const empty: TitleZoneStyle = { fontFamilyRaw: null, fontFamilyName: null, colorHex: null };
  if (!zones || zones.length === 0) return empty;
  const idx = zones.findIndex((z) => z?.type === 'destination');
  if (idx < 0) return empty;
  const zone = zones[idx] ?? {};
  // The PDF passes a Record keyed by number OR String(i); cover both.
  const o = overrides?.[idx] ?? overrides?.[String(idx)] ?? {};
  // Mirror the title zone's own resolution chain (override ?? template), the
  // same `??` semantics CoverThumbnail uses for `effectiveFontFamily`/color.
  const fontFamilyRaw = (o?.fontFamily ?? zone?.font_family) ?? null;
  const fontFamilyName = resolveZoneFontName(o?.fontFamily, zone?.font_family);
  const colorHex = (o?.color ?? zone?.color) ?? null;
  return { fontFamilyRaw, fontFamilyName, colorHex };
}

// Turn a stored font value (bare name or CSS family list) into a CSS
// font-family string. MUST stay byte-identical to CoverThumbnail's private
// `toCssFontFamily` so the spine renders the title's exact typeface in the
// browser. A bare name gets quoted + a generic fallback; a list passes
// through; null/empty → `inherit`.
export function toCssFontFamily(family: string | null | undefined): string {
  if (!family) return 'inherit';
  return /[,'"]/.test(family) ? family : `"${family}", sans-serif`;
}

export async function loadFontBytes(name: string): Promise<ArrayBuffer | null> {
  if (fontBytesCache.has(name)) return fontBytesCache.get(name) ?? null;
  const url = FONT_URLS[name];
  if (!url) { fontBytesCache.set(name, null); return null; }
  try {
    const r = await fetch(url);
    if (!r.ok) { fontBytesCache.set(name, null); return null; }
    const buf = await r.arrayBuffer();
    fontBytesCache.set(name, buf);
    return buf;
  } catch {
    fontBytesCache.set(name, null);
    return null;
  }
}
